import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import startCase from 'lodash/startCase';
import { connect } from 'react-redux';


import { getPost, getRelatedPosts } from 'Redux/ducks/blog';
import BlogPost from 'Components/Blog/BlogPostPage';
import NotFound from 'Components/NotFound/NotFound';
import fetchFacetData from 'Components/FacetSearch/facetFetch';
import { fetchProductBySlug, fetchBySlugs, fetchTopSellersExclude, query, fetchBundles } from 'Redux/ducks/algolia-calls';


const BlogPostContainer = ({ match, authenticated }) => {
  const [data, setData] = useState({ post: {}, relatedPosts: [], buyNowProduct: {} });
  const [hasLoaded, setHasLoaded] = useState(false);

  const { params: { slug } } = match;

  useEffect(() => {
    const loadPost = async (postSlug) => {
      if (!slug) return false;

      const post = await getPost(postSlug);

      const buyNowProductSlug = get(post, 'data.buy_now_product_slug[0].text', '');
      const slices = get(post, 'data.body', []);

      const productCards = slices.filter(slice => slice.slice_type === 'product_card');

      const productSlugs = productCards.map(slice => get(slice, 'primary.slice_text[0].text')).filter(s => s);

      const imageGalleryProductCards = flatMap(slices.filter(slice => slice.slice_type === 'image_gallery'), slice => get(slice, 'items', []).map(item => get(item, 'product_card[0]')).filter(f => f));
      const videoGalleryProductCards = flatMap(slices.filter(slice => slice.slice_type === 'gleam_widget'), slice => get(slice, 'items', []).map(item => get(item, 'product_card[0]')).filter(f => f));

      const imageGalleryProductSlugs = imageGalleryProductCards.map(slice => get(slice, 'text')).filter(s => s);
      const videoGalleryProductSlugs = videoGalleryProductCards.map(slice => get(slice, 'text')).filter(s => s);

      productCards.push(...imageGalleryProductCards, ...videoGalleryProductCards);
      productSlugs.push(...imageGalleryProductSlugs, ...videoGalleryProductSlugs);

      if (buyNowProductSlug) {
        productSlugs.push(buyNowProductSlug);
      }

      const searchTerm = get(post, 'data.search_term[0].text', '');
      const facets = ['collections', 'franchises', 'publishers', 'categories', 'themes', 'playstyles']; // Themes and playstyles are legacy, should get converted to categories further down
      const facetsObj = {};
      let hasFacets = false;

      facets.forEach((facet) => {
        const rawSlugs = get(post, `data.${facet}_slugs`, []).map(f => f.slug).filter(f => f);
        if (rawSlugs.length) {
          hasFacets = true;
        }
        facetsObj[facet] = rawSlugs;
      });

      // Text and link for
      let viewMoreLabel = 'View More';
      let viewMoreLink = '/top-sellers';

      // Fetch related posts
      const promiseRelated = async () => getRelatedPosts(post.id, 5);

      // Fetch products for post body
      const promiseProductHits = async () => {
        if (productSlugs.length) {
          const hits = await fetchBySlugs([productSlugs]);

          productCards.forEach((slice) => {
            const hitSlug = get(slice, 'primary.slice_text[0].text', get(slice, 'text'));
            slice.hit = hits[0].find(h => h.slug === hitSlug); // eslint-disable-line no-param-reassign,max-len
          });

          if (buyNowProductSlug) {
            return hits[0].find(h => h.slug === buyNowProductSlug);
          }
        }

        if (buyNowProductSlug) {
          return fetchProductBySlug(buyNowProductSlug);
        }
        return null;
      };

      // Fetch related products to go in sidebar and footer (excluding products already in body)
      const promiseRelatedProducts = async () => {
        if (hasFacets) {
          let facetSlug;
          let filteredFacet;
          facets.some((facet) => {
            if (facetsObj[facet] && facetsObj[facet].length) {
              filteredFacet = facet.replace(/themes|playstyles/, 'categories');
              facetSlug = facetsObj[facet][0];
              viewMoreLabel = `View more ${startCase(facetSlug)}`;
              viewMoreLink = `/${filteredFacet}/${facetSlug}`;

              return true;
            }
            return false;
          });
          return fetchFacetData(filteredFacet, facetSlug)
            .then(({ fetch }) => fetch().then(({ hits }) =>
              hits.filter(h => !productSlugs.includes(h.slug)),
            ));
        }
        if (searchTerm) {
          if (searchTerm === 'bundle' || searchTerm === 'bundles') {
            viewMoreLabel = 'View more Bundles';
            viewMoreLink = '/bundle';
            return fetchBundles(6).then(result => result.hits);
          }
          if (searchTerm === 'vr') {
            viewMoreLabel = 'View more VR Games';
            viewMoreLink = '/vr-games';
            return query('vr', 6, productSlugs);
          }

          viewMoreLabel = `View more ${startCase(searchTerm)}`;
          viewMoreLink = `/search?search=${searchTerm}`;
          return query(searchTerm, 6, productSlugs);
        }
        return fetchTopSellersExclude(6, productSlugs);
      };

      const [relatedPosts, buyNowProduct, relatedProducts] = await Promise.all([
        promiseRelated(),
        promiseProductHits(),
        promiseRelatedProducts(),
      ]);

      setData({ post, relatedPosts, buyNowProduct, relatedProducts, viewMoreLabel, viewMoreLink });
      return setHasLoaded(true);
    };

    loadPost(slug);
  }, [slug]);

  const { post, relatedPosts, buyNowProduct, relatedProducts, viewMoreLabel, viewMoreLink } = data;

  if (hasLoaded && isEmpty(post)) return <NotFound />;

  if (isEmpty(post)) return false;
  return (
    <BlogPost
      post={post}
      relatedPosts={relatedPosts}
      buyNowProduct={buyNowProduct}
      relatedProducts={relatedProducts}
      viewMoreLabel={viewMoreLabel}
      viewMoreLink={viewMoreLink}
      authenticated={authenticated}
    />
  );
};

BlogPostContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string,
    }),
  }),

};

BlogPostContainer.defaultProps = {
  match: {
    params: {
      slug: '',
    },
  },
};

const mapStateToProps = ({
  auth: { authenticated },
}) => ({
  authenticated,
});

export default connect(mapStateToProps)(BlogPostContainer);
